import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import style from './AddRoutinePage.module.css';
import RadioButton from '../../components/RadioButton/RadioButton';
import FormInput from '../../components/FormInput/FormInput';
import AddChallengeCard from '../../components/AddChallengeCard/AddChallengeCard';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import UpgradePrompt from '../../components/UpgradePrompt/UpgradePrompt';
import SecondaryButton from '../../components/SecondaryButton/SecondaryButton';
import ChallengeCard from '../../components/ChallengeCard/ChallengeCard';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import formatDate from '../../utils/formatDate';
import { checkSubscription } from '../../utils/checkSubscription';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuthUser } from '../../hooks/useAuthUser';
import { UserContext } from '../../contexts/UserContext';
import {
	MdFitnessCenter,
	MdTimer,
	MdArrowBack,
	MdSportsGymnastics,
	MdAdd,
	MdCheck,
} from 'react-icons/md';

const API_URL = process.env.REACT_APP_API_URL;

const AddRoutinePage = () => {
	const { user } = useAuthUser();
	const { dbUser } = useContext(UserContext);
	useTheme();
	const navigate = useNavigate();

	useEffect(() => {
		if (!user) {
			navigate(`/login?redirectTo=${encodeURIComponent('/add-routine')}`, { replace: true });
		}
	}, [user, navigate]);

	const fitnessLevelArray = ['Beginner', 'Intermediate', 'Advanced'];
	const durationArray = ['30', '45', '60'];
	const [routinesArray, setRoutineArray] = useState([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [routineAttributes, setRoutineAttributes] = useState({
		level: '',
		estimatedTimeMinutes: '',
		restDays: '',
		routineName: '',
		routineDescription: '',
		routineEquipments: '',
		workouts: [],
		createdDate: formatDate(),
		routineCreationType: 'created',
	});

	if (user && !checkSubscription(dbUser)) {
		return (
			<UpgradePrompt
				heading="Creating routines is a premium feature"
				description="Upgrade to unlock routine creation and more advanced features."
				onUpgrade={() => navigate('/upgrade')}
			/>
		);
	}

	const addStryds = async () => {
		const { workouts, level, estimatedTimeMinutes, routineName } = routineAttributes;
		if (!routineName) { toast('Routine name is required.'); return; }
		if (!level) { toast('Please select a fitness level.'); return; }
		if (!estimatedTimeMinutes) { toast('Please select a duration.'); return; }
		if (!workouts || workouts.length === 0) { toast('Add at least one workout to your routine.'); return; }

		setIsSubmitting(true);

		const userToAdd = {
			userID: user.id,
			username: user.username,
			profileImageLink: user.profileImageUrl,
			userRoutineXP: 0,
		};

		try {
			toast('Creating your routine...');
			await axios.post(`${API_URL}/stryds/${user.id}`, routineAttributes);
			await axios.post(
				`${API_URL}/stryds/join-routine/${user.id}/${routineAttributes.routineName}`,
				userToAdd
			);
			toast('Routine created successfully!');
			navigate('/profile-page');
		} catch (error) {
			console.error(error.response?.data || error.message);
			toast('Failed to create routine. Please try again.');
			setIsSubmitting(false);
		}
	};

	const handleAddRoutine = newChallengeData => {
		setRoutineArray(prevRoutinesArray => {
			const updatedRoutinesArray = [...prevRoutinesArray, newChallengeData];
			setRoutineAttributes(prev => ({ ...prev, workouts: updatedRoutinesArray }));
			return updatedRoutinesArray;
		});
	};

	const handleRoutineDescriptionChange = event => {
		setRoutineAttributes(prev => ({ ...prev, routineDescription: event.target.value }));
	};

	const handleRoutineEquipmentsChange = event => {
		setRoutineAttributes(prev => ({ ...prev, routineEquipments: event.target.value }));
	};

	const handleRoutineNameChange = event => {
		setRoutineAttributes(prev => ({ ...prev, routineName: event.target.value }));
	};

	const handleLevelChange = (level) => () => {
		setRoutineAttributes(prev => ({ ...prev, level }));
	};

	const handleDurationChange = (estimatedTimeMinutes) => () => {
		setRoutineAttributes(prev => ({ ...prev, estimatedTimeMinutes }));
	};

	const deleteRoutine = index => {
		setRoutineArray(prevRoutinesArray => {
			const updatedRoutinesArray = [...prevRoutinesArray];
			updatedRoutinesArray.splice(index, 1);
			setRoutineAttributes(prev => ({ ...prev, workouts: updatedRoutinesArray }));
			return updatedRoutinesArray;
		});
	};

	const getFormProgress = () => {
		const fields = [
			routineAttributes.routineName,
			routineAttributes.level,
			routineAttributes.estimatedTimeMinutes,
			routineAttributes.routineEquipments,
			routinesArray.length > 0,
		];
		const completed = fields.filter(Boolean).length;
		return (completed / fields.length) * 100;
	};

	const progress = getFormProgress();

	return (
		<div className={style.AddRoutinePage}>
			{user ? (
				<>
					<div className={style.Header}>
						<button className={style.BackButton} onClick={() => navigate(-1)}>
							<MdArrowBack />
							<span>Back</span>
						</button>
						<div className={style.HeaderContent}>
							<div className={style.CreateBadge}>
								<MdAdd />
								<span>Manual</span>
							</div>
							<h1 className={style.PageTitle}>Create Your Routine</h1>
							<p className={style.PageDescription}>
								Build a custom workout routine with full control over every detail.
							</p>
						</div>
					</div>

					<div className={style.ProgressSection}>
						<div className={style.ProgressInfo}>
							<div className={style.ProgressBar}>
								<div className={style.ProgressFill} style={{ width: `${progress}%` }} />
							</div>
							<span className={style.ProgressText}>
								{progress === 100 ? (
									<><MdCheck className={style.ProgressCheck} /> Ready to save</>
								) : (
									`${Math.round(progress)}% Complete`
								)}
							</span>
						</div>
					</div>

					<div className={style.Content}>
						<div className={style.Section}>
							<div className={style.SectionHeader}>
								<MdFitnessCenter className={style.SectionIcon} />
								<h2 className={style.SectionTitle}>Routine Details</h2>
							</div>
							<div className={style.FormGrid}>
								<div className={style.FormGroup}>
									<label className={style.FormLabel}>
										Routine Name <span className={style.Required}>*</span>
									</label>
									<FormInput HeadingText="" placeholder="e.g. Morning Strength"
										change={handleRoutineNameChange} width="100%" showIsEmpty={true} />
								</div>
								<div className={style.FormGroup}>
									<label className={style.FormLabel}>Description</label>
									<FormInput HeadingText="" placeholder="Optional description of your routine"
										change={handleRoutineDescriptionChange} width="100%" showIsEmpty={true} />
								</div>
								<div className={style.FormGroupFull}>
									<label className={style.FormLabel}>
										Equipment Needed <span className={style.Required}>*</span>
									</label>
									<FormInput HeadingText="" placeholder="e.g. Dumbbells, Pull-Up Bar (or 'None' for bodyweight)"
										change={handleRoutineEquipmentsChange} width="100%" showIsEmpty={true} />
								</div>
							</div>
						</div>

						<div className={style.Section}>
							<div className={style.SectionHeader}>
								<MdTimer className={style.SectionIcon} />
								<h2 className={style.SectionTitle}>Workout Settings</h2>
							</div>
							<div className={style.SettingsGrid}>
								<div className={style.SettingGroup}>
									<label className={style.SettingLabel}>
										Fitness Level <span className={style.Required}>*</span>
									</label>
									<div className={style.RadioGroup}>
										{fitnessLevelArray.map((level) => (
											<RadioButton key={level} label={level} value={level} name="levels"
												isChecked={routineAttributes.level === level}
												onRadioChange={handleLevelChange(level)} />
										))}
									</div>
								</div>
								<div className={style.SettingGroup}>
									<label className={style.SettingLabel}>
										Duration <span className={style.Required}>*</span>
									</label>
									<div className={style.RadioGroup}>
										{durationArray.map((duration) => (
											<RadioButton key={duration} label={`${duration} mins`} value={duration}
												name="durations" isChecked={routineAttributes.estimatedTimeMinutes === duration}
												onRadioChange={handleDurationChange(duration)} />
										))}
									</div>
								</div>
							</div>
						</div>

						<div className={style.Section}>
							<div className={style.SectionHeader}>
								<MdSportsGymnastics className={style.SectionIcon} />
								<h2 className={style.SectionTitle}>Exercises</h2>
								<span className={style.ExerciseCount}>
									{routinesArray.length} {routinesArray.length === 1 ? 'exercise' : 'exercises'}
								</span>
							</div>

							<div className={style.WorkoutsContainer}>
								{routinesArray.length > 0 && (
									<div className={style.WorkoutsList}>
										{routinesArray.map((routine, index) => (
											<ChallengeCard
												key={index}
												workoutName={routine.workoutType}
												workoutSets={routine.workoutSets}
												workoutReps={routine.workoutReps}
												deleteable={true}
												delete={() => deleteRoutine(index)}
											/>
										))}
									</div>
								)}

								<div className={style.AddWorkoutSection}>
									<AddChallengeCard addRoutine={handleAddRoutine} />
								</div>
							</div>
						</div>
					</div>

					<div className={style.Actions}>
						<SecondaryButton text="Cancel" click={() => navigate(-1)} />
						<PrimaryButton
							text={isSubmitting ? 'Creating...' : 'Save Routine'}
							click={addStryds}
							disabled={isSubmitting}
							loading={isSubmitting}
						/>
					</div>
				</>
			) : (
				<div className={style.AuthMessage}>
					<span>Please log in to create routines</span>
				</div>
			)}
		</div>
	);
};

export default AddRoutinePage;
